// packages/ui/src/components/chat/ChatMessageList.tsx
'use client';

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { TFunc } from '@tourtask/types/i18n';
import type { ChatMessage, ChatSenderType } from '@tourtask/chat-domain';
import { parseSystemNotice } from '@tourtask/chat-domain';
import { transparencyCheckerboardSx } from '../../styles/transparencyCheckerboardSx';
import { ExactValue } from '../ExactValue';
import { ChatMessageBody } from './ChatMessageBody';

interface ChatMessageListProps {
  messages: readonly ChatMessage[];
  /** The viewer's own sender type — their messages render right-aligned. */
  ownSenderType: ChatSenderType;
  /** The viewer's agent id, when they are an agent — used to colour an
   *  "agent joined" chip differently for themselves vs. another agent. */
  currentAgentUserId?: number;
  t: TFunc;
  locale?: string;
  /** Business-local timezone for the message clock, when known. */
  timezone?: string;
  emptyLabel?: string;
  /** An optional secondary line under `emptyLabel` (e.g. a "support is offline"
   *  notice) — only rendered in the empty state, when provided. */
  emptySubLabel?: string;
  /** Show an animated "…is typing" bubble at the foot of the list (the bot is
   *  composing a reply / a turn is in flight). */
  typing?: boolean;
  /** The counterpart's read cursor, for desk consoles: when set (a number), every
   *  own-side message gets a small read receipt in its bottom-right corner —
   *  green check = read (`id <= cursor`), gray check = unread. `null`/`undefined`
   *  = no receipts (customer-facing surfaces and threads without read tracking
   *  never pass it — see chat-domain `resolveReadReceiptCursor`). */
  readReceiptCursor?: number | null;
}

/** The per-message read receipt: a small circular check overlapping the bubble's
 *  bottom-right corner, green when the counterpart has read it, gray when not.
 *  Hover explains it ("Read" / "Unread"). */
function ReadReceiptBadge({ read, t }: { read: boolean; t: TFunc }) {
  return (
    <Tooltip title={read ? t('Read') : t('Unread')}>
      <Box
        sx={{
          position: 'absolute',
          bottom: -5,
          right: -5,
          width: 16,
          height: 16,
          borderRadius: '50%',
          // A paper backdrop ring so the check reads on the colored bubble corner.
          bgcolor: 'background.paper',
          boxShadow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 14, color: read ? 'success.main' : 'grey.500' }} />
      </Box>
    </Tooltip>
  );
}

/** The three-dot "typing…" bubble, styled like an incoming message bubble. */
function TypingBubble() {
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '80%' }}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderRadius: 2,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '@keyframes ttChatTypingBounce': {
            '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
            '40%': { transform: 'translateY(-3px)', opacity: 1 },
          },
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'text.secondary',
              animation: 'ttChatTypingBounce 1.2s infinite ease-in-out',
              animationDelay: `${i * 0.16}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/** Messages within this window of the previous same-sender message belong to the
 *  same "burst" — only the last of a burst shows a timestamp. */
const SAME_GROUP_MS = 2 * 60 * 1000;

function formatTime(iso: string, locale?: string, timezone?: string): string {
  try {
    return new Date(iso).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return '';
  }
}

/** A message shows its time only if it ends a burst: it's the last message, or
 *  the next one is a different sender / a system line / ≥2 min later. */
function isLastOfTimeGroup(messages: readonly ChatMessage[], i: number): boolean {
  const cur = messages[i];
  const next = messages[i + 1];
  if (!next) return true;
  if (next.senderType !== cur.senderType) return true;
  const gap = new Date(next.createdAtUtc).getTime() - new Date(cur.createdAtUtc).getTime();
  return !Number.isFinite(gap) || gap >= SAME_GROUP_MS;
}

/**
 * The shared conversation message list — used by both the backoffice widget and
 * the admin thread view (docs/chat-architecture.md §6 "one shared conversation
 * pane"). Pure presentational: data + `t` in, no transport/DB/next-intl.
 */
export function ChatMessageList({
  messages,
  ownSenderType,
  currentAgentUserId,
  t,
  locale,
  timezone,
  emptyLabel,
  emptySubLabel,
  typing,
  readReceiptCursor,
}: ChatMessageListProps) {
  const endRef = useRef<HTMLDivElement | null>(null);
  // The full-size image lightbox (open on attachment click — never a new tab).
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Re-scroll to the newest message whenever the count grows or typing toggles.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, typing]);

  if (messages.length === 0 && !typing) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {emptyLabel ?? t('No_messages_yet')}
        </Typography>
        {emptySubLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {emptySubLabel}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        // Never let content cause a horizontal scrollbar — long/unbreakable text
        // wraps (see the bubble/notice/chip styles below); this clips as a backstop.
        overflowX: 'hidden',
        minWidth: 0,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {messages.map((m, i) => {
        if (m.senderType === 'system') {
          const notice = parseSystemNotice(m.body);
          if (notice.kind === 'agent_joined') {
            // Agent surface (currentAgentUserId set) → a name chip coloured "me"
            // vs "another agent". Customer surface (widget, no agent id) → a
            // plain localized "{name} entered the chat" sentence.
            if (currentAgentUserId == null) {
              return (
                <Box key={m.id} sx={{ alignSelf: 'center', maxWidth: '90%', textAlign: 'center', my: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('X_entered_the_chat', { name: notice.name })}
                  </Typography>
                </Box>
              );
            }
            const isMe = notice.agentUserId === currentAgentUserId;
            return (
              <Box key={m.id} sx={{ alignSelf: 'center', my: 0.5 }}>
                <Chip
                  size="small"
                  icon={<PersonIcon />}
                  label={notice.name}
                  sx={{
                    bgcolor: isMe ? 'primary.main' : 'grey.300',
                    color: isMe ? 'primary.contrastText' : 'text.primary',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Box>
            );
          }
          if (notice.kind === 'ai_handoff') {
            // The AI→human handoff summary is for the AGENT only. On a customer
            // surface (no agent id) show a neutral chip — NEVER the internal
            // summary. On an agent surface, show the full summary.
            if (currentAgentUserId == null) {
              return (
                <Box key={m.id} sx={{ alignSelf: 'center', maxWidth: '100%', my: 0.5 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={t('Handover_requested')}
                    // A chip's label is nowrap by default — a long/unseeded string
                    // would widen it past the panel. Let it wrap instead.
                    sx={{
                      height: 'auto',
                      maxWidth: '100%',
                      '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.25 },
                    }}
                  />
                </Box>
              );
            }
            return (
              <Box key={m.id} sx={{ alignSelf: 'center', maxWidth: '95%', my: 0.5 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'warning.light',
                    bgcolor: 'rgba(237,108,2,0.08)',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    {t('Handover_summary')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', display: 'block' }}
                  >
                    {notice.text}
                  </Typography>
                </Box>
              </Box>
            );
          }
          return (
            <Box key={m.id} sx={{ alignSelf: 'center', maxWidth: '90%', textAlign: 'center', my: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic', overflowWrap: 'anywhere' }}
              >
                {notice.text}
              </Typography>
            </Box>
          );
        }
        // The AI bot answers on the support side. On an agent console
        // (currentAgentUserId set) show its replies on the viewer's own side,
        // next to the agent's own replies — but in a distinct colour (below) so
        // AI vs. human is obvious at a glance. On a customer surface (widget, no
        // agent id) the AI is the incoming responder and is left unchanged.
        const aiOnSupportSide = m.senderType === 'ai' && currentAgentUserId != null;
        const own = m.senderType === ownSenderType || aiOnSupportSide;
        const showTime = isLastOfTimeGroup(messages, i);
        const images = m.attachments.filter((a) => a.kind === 'image');
        const hasBody = m.body.trim() !== '';
        // Read receipt on the desk's own outgoing messages only (never on the
        // customer's side, never on an optimistic negative-id placeholder).
        const receiptRead = readReceiptCursor != null && own && m.id > 0 ? m.id <= readReceiptCursor : null;
        return (
          <Box
            key={m.id}
            sx={{
              alignSelf: own ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              // Tighten grouped bubbles; spacing returns after a timestamp.
              mb: showTime ? 0 : -0.5,
            }}
          >
            {/* Relative wrapper so the receipt badge anchors to the content's
                bottom-right corner whether the message is a bubble, an image, or
                both — the timestamp below stays outside it. */}
            <Box sx={{ position: 'relative' }}>
              {images.map((a) => (
                <Box
                  key={a.id}
                  component="img"
                  src={a.url}
                  alt=""
                  onClick={() => setLightboxUrl(a.url)}
                  sx={{
                    display: 'block',
                    mb: hasBody ? 0.5 : 0,
                    maxWidth: 240,
                    maxHeight: 280,
                    width: 'auto',
                    height: 'auto',
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                  }}
                />
              ))}
              {hasBody && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    // AI bubbles get their own colour; agent's own replies stay
                    // primary; the customer/other side stays neutral grey.
                    bgcolor: aiOnSupportSide ? 'secondary.main' : own ? 'primary.main' : 'grey.100',
                    color: aiOnSupportSide
                      ? 'secondary.contrastText'
                      : own
                        ? 'primary.contrastText'
                        : 'text.primary',
                    wordBreak: 'break-word',
                    // A single very long token (e.g. a pasted URL) must wrap too,
                    // never widen the bubble past the panel.
                    overflowWrap: 'anywhere',
                  }}
                >
                  {/* Light-Markdown render: clickable new-tab links + small tables.
                      pre-wrap (newline preservation) now lives per text block in
                      ChatMessageBody, not on this bubble. */}
                  <ChatMessageBody body={m.body} own={own} />
                </Box>
              )}
              {receiptRead !== null && <ReadReceiptBadge read={receiptRead} t={t} />}
            </Box>
            {showTime && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: own ? 'right' : 'left', mt: 0.25 }}
              >
                {/* The embed's chat launcher runs on partner pages a browser may translate: a time is a value island. */}
                <ExactValue>{formatTime(m.createdAtUtc, locale, timezone)}</ExactValue>
              </Typography>
            )}
          </Box>
        );
      })}
      {typing && <TypingBubble />}
      <div ref={endRef} />

      <Dialog
        open={lightboxUrl !== null}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        slotProps={{ paper: { sx: { bgcolor: 'common.black', m: 2, overflow: 'visible' } } }}
      >
        <IconButton
          onClick={() => setLightboxUrl(null)}
          aria-label={t('Close')}
          sx={{
            position: 'absolute',
            top: -16,
            right: -16,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <CloseIcon />
        </IconButton>
        {lightboxUrl !== null && (
          // On the checkerboard, so a transparent image is seen as transparent, not on the dialog's black.
          <Box
            component="img"
            src={lightboxUrl}
            alt=""
            sx={[
              transparencyCheckerboardSx,
              {
                display: 'block',
                maxWidth: '90vw',
                maxHeight: '85vh',
                width: 'auto',
                height: 'auto',
              },
            ]}
          />
        )}
      </Dialog>
    </Box>
  );
}
